import { Box, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { ProvenanceLegend } from './ProvenanceFooter'

/**
 * The heading every screen was missing.
 *
 * Before this, a screen opened directly into a `<Panel>` whose title rendered
 * as `variant="h4"` — so no page had an `<h1>`, the document outline was empty,
 * and a screen reader landed in the middle of a table with no idea what it was
 * looking at. The `pageTitle` variant maps to `h1` (see
 * `theme/createInternalTheme`), which is what makes this fix structural rather
 * than typographic.
 *
 * ── THE DESCRIPTION IS NOT DECORATION ───────────────────────────────────────
 * On most screens in this app it is the sentence that says what the numbers
 * below do NOT mean — that "active" is not a login, that bytes are estimated,
 * that a list value is not money that came in. It sits at the top because a
 * reader who scrolls straight to a table should have passed it on the way.
 */
export interface PageHeaderProps {
  /** The section this screen belongs to. Renders above the title. */
  eyebrow?: string
  title: string
  description?: ReactNode
  /** Filters, range pickers, links out. Right-aligned, wraps below on narrow. */
  actions?: ReactNode
  /**
   * The four-word provenance key. Defaults to the legend; pass `null` on a
   * screen with no data, where a key to words nothing uses is just noise.
   *
   * It moved here out of the top bar. In the shell it sat in the row the eye
   * scans for navigation and competed with it; one level down it is still on
   * every page and still inside every full-page screenshot, and it now sits
   * adjacent to the chips it explains. It stays visible at every width — four
   * 18px chips — because the whole argument for it is that a screenshot must
   * carry its own epistemology, and a screenshot taken on a laptop is still a
   * screenshot.
   */
  provenance?: ReactNode | null
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  provenance = <ProvenanceLegend />,
}: PageHeaderProps) {
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        mb: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {eyebrow && (
          <Typography variant="eyebrow" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {eyebrow}
          </Typography>
        )}
        <Typography variant="pageTitle">{title}</Typography>
        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.75, maxWidth: '78ch' }}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}
      >
        {actions}
        {provenance}
      </Stack>
    </Box>
  )
}
